import { useState, useRef } from "react";
import axios from "axios";
import { FileUp } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import ShweebumCat from "./ShweebumCat";
import { API } from "../lib/api";

const ImportDialog = ({ open, onOpenChange, projectId, onImported, hasContent = false }) => {
  const [importing, setImporting] = useState(false);
  const [mode, setMode] = useState("replace");
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const endpoint = file.name.endsWith(".pdf") ? "/import/pdf" : "/import/text";
      const res = await axios.post(`${API}${endpoint}?project_id=${projectId}&mode=${hasContent ? mode : "replace"}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      onImported(res.data.text);
      const ch = res.data.chapters_detected || 0;
      if (ch > 0) {
        toast.success(`Imported ${res.data.word_count} words — ${ch} chapters detected and synced to your outline & audio!`, { duration: 6000 });
      } else {
        toast.success(`Imported ${res.data.word_count} words! (No chapter headings found — audio will read it as one piece)`, { duration: 6000 });
      }
      onOpenChange(false);
    } catch (error) {
      toast.error("Failed to import file");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-literary flex items-center gap-2">
            <ShweebumCat size="sm" />
            Import Your Work
          </DialogTitle>
          <DialogDescription>
            Shweebum will read your file and help you continue
          </DialogDescription>
        </DialogHeader>
        <div className="py-6">
          {hasContent && (
            <div className="mb-4 space-y-2" data-testid="import-mode-picker">
              <p className="text-xs text-muted-foreground">This project already has story content. What should the import do?</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={mode === "replace" ? "default" : "outline"}
                  className="flex-1 text-xs"
                  onClick={() => setMode("replace")}
                  data-testid="import-mode-replace"
                >
                  Replace entire story
                </Button>
                <Button
                  size="sm"
                  variant={mode === "append" ? "default" : "outline"}
                  className="flex-1 text-xs"
                  onClick={() => setMode("append")}
                  data-testid="import-mode-append"
                >
                  Add after last chapter
                </Button>
              </div>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.pdf"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />
          <Button 
            variant="outline" 
            className="w-full h-32 border-dashed flex flex-col items-center gap-2"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="upload-area"
          >
            {importing ? (
              <>
                <div className="typing-indicator">
                  <span>.</span><span>.</span><span>.</span>
                </div>
                <span>Shweebum is reading...</span>
              </>
            ) : (
              <>
                <FileUp className="w-8 h-8 text-muted-foreground" />
                <span className="text-muted-foreground">Click to upload .txt or .pdf</span>
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportDialog;
